// 144.3. Composing a standard move that several units make together.
//
// "Players may perform multiple Units' standard move simultaneously. This is
// treated as one game action performed on multiple Units" -- same destination,
// origins need not match, and the exhaust costs are paid at once.
//
// This has to be *composed* rather than picked off the engine's action list.
// The engine enumerates every single-unit move and the whole-army move, but
// not the subsets between them: offering those would multiply the action list
// by 2^n. And the subsets cannot be reached by moving one unit at a time,
// which was the assumption that produced the bug -- a completed move ends in a
// cleanup (453) that stages the combat, and 144.1.c forbids a standard move
// during a Showdown or Combat. Sending one unit into a contested battlefield
// committed it alone and locked the rest of the army out of the fight.
//
// Every destination offered here comes from an enumerated single-unit move,
// for every unit in the group, so this can never light up a square the engine
// will refuse. It is pure so it can be tested without a GPU: the failure mode
// is a destination silently missing from the intersection, which on a board is
// indistinguishable from a unit that simply cannot go there.

import type { Action, Location, ObjectId } from '../rules/types.ts';

export type Move = Extract<Action, { t: 'move' }>;

/**
 * A location as a string, so sets of destinations can be intersected.
 *
 * `sameLocation` answers about a pair; this asks which destinations several
 * units have in common, which wants a key.
 */
export function placeKey(at: Location): string {
  return at.kind === 'battlefield' ? `bf:${at.index}` : `base:${at.player}`;
}

/**
 * Where one unit could go on its own.
 *
 * Only the singletons. A group action in the list names several units, and
 * reading a destination off it would offer this unit a square it can only
 * reach in company.
 */
export function soloDestinations(
  moves: readonly Move[], id: ObjectId,
): Map<string, Location> {
  const out = new Map<string, Location>();
  for (const a of moves) {
    if (a.objects.length !== 1 || a.objects[0] !== id) continue;
    out.set(placeKey(a.to), a.to);
  }
  return out;
}

/**
 * 144.3.a. The destinations every one of these units can reach.
 *
 * An empty group has no destinations rather than all of them: "nothing is
 * selected" must not light up the whole board.
 */
export function sharedDestinations(
  moves: readonly Move[], ids: readonly ObjectId[],
): Map<string, Location> {
  if (ids.length === 0) return new Map();
  let shared: Map<string, Location> | null = null;
  for (const id of ids) {
    const mine = soloDestinations(moves, id);
    if (shared === null) { shared = mine; continue; }
    for (const key of [...shared.keys()]) if (!mine.has(key)) shared.delete(key);
  }
  return shared ?? new Map();
}

/**
 * Which other units could join the group as it stands.
 *
 * A unit qualifies when it shares at least one destination with the group --
 * so adding it narrows the shared set rather than emptying it. That is why the
 * offer is recomputed after every change instead of taken once at the start: a
 * unit that could have joined the opening selection may not be able to join it
 * two units later.
 */
export function joinable(
  moves: readonly Move[], group: readonly ObjectId[], candidates: Iterable<ObjectId>,
): Set<ObjectId> {
  const out = new Set<ObjectId>();
  const shared = sharedDestinations(moves, group);
  if (shared.size === 0) return out;
  for (const id of candidates) {
    if (group.includes(id)) continue;
    for (const key of soloDestinations(moves, id).keys()) {
      if (shared.has(key)) { out.add(id); break; }
    }
  }
  return out;
}

/**
 * The action a drop on this destination submits.
 *
 * Composed rather than looked up, because the engine does not enumerate it.
 * `doMove` validates it unit by unit against the same test that produced the
 * singletons, so composing here cannot make an illegal move legal.
 */
export function groupMove(group: readonly ObjectId[], to: Location): Move {
  return { t: 'move', objects: [...group], to };
}
